import { useMutation, gql, useLazyQuery } from '@apollo/client'
import {
  RetailerAccount,
  Automation,
  AdAutomation,
  Query,
  Mutation,
  CreateAdAutomationsInput,
  UpdateAdAutomationsInput,
  MutationUpdateAutomationsArgs,
  MutationCreateAutomationsArgs,
  UpdateAutomationInput,
} from '@promoboxx/graphql-gateway-types'
import { useEffect, useMemo, useCallback, useState } from 'react'

///////////////////////////////////////////////////////////////////////////////
// ORGANIC AUTOMATIONS (GET is included on FacebookPage)
///////////////////////////////////////////////////////////////////////////////
const GET_AUTOMATIONS = gql`
  query getAutomationsUseAutomations(
    $resourceType: AutomationResourceType!
    $resourceId: ID!
    $params: [AutomationParams!]
  ) {
    getAutomations(
      resourceType: $resourceType
      resourceId: $resourceId
      params: $params
    ) {
      id
      retailer_id
      category
      automationable_id
      automationable_type
      executed_at
      created_at
      updated_at
      failed_at
      deleted_at
      enabled
      status
    }
  }
`

const CREATE_AUTOMATION = gql`
  mutation createAutomationUseAutomations(
    $retailerId: ID!
    $automation: CreateAutomationInput!
  ) {
    createAutomation(retailerId: $retailerId, automation: $automation) {
      id
      retailer_id
      category
      automationable_id
      automationable_type
      executed_at
      created_at
      updated_at
      failed_at
      deleted_at
      enabled
      status
    }
  }
`

const UPDATE_AUTOMATION = gql`
  mutation updateAutomationUseAutomations(
    $automationId: ID!
    $state: AutomationState!
  ) {
    updateAutomation(automationId: $automationId, state: $state) {
      automationId
      state
    }
  }
`

const CREATE_AUTOMATIONS = gql`
  mutation createAutomationsUseAutomations(
    $params: [CreateAutomationsInput!]!
  ) {
    createAutomations(params: $params) {
      id
      retailer_id
      category
      automationable_id
      automationable_type
      executed_at
      created_at
      updated_at
      failed_at
      deleted_at
      enabled
      status
    }
  }
`

const UPDATE_AUTOMATIONS = gql`
  mutation updateAutomationsUseAutomations($params: [UpdateAutomationInput!]!) {
    updateAutomations(params: $params) {
      automationId
      state
    }
  }
`

///////////////////////////////////////////////////////////////////////////////
// PAID AD AUTOMATIONS
///////////////////////////////////////////////////////////////////////////////
const GET_AD_AUTOMATIONS_BY_RETAILER_IDS = gql`
  query getAdAutomationsByRetailerIdsUseAutomations($retailerIds: [ID!]!) {
    getAdAutomationsByRetailerIds(retailerIds: $retailerIds) {
      brand_id
      channel
      customization {
        channel
        facebook_page_id
        geo_locations {
          custom_locations {
            radius
            latitude
            longitude
          }
        }
      }
      id
      retailer_id
      state
    }
  }
`

const CREATE_AD_AUTOMATION = gql`
  mutation createAdAutomationUseAutomations(
    $automation: CreateAdAutomationInput!
  ) {
    createAdAutomation(automation: $automation) {
      id
      state
    }
  }
`

const UPDATE_AD_AUTOMATION = gql`
  mutation updateAdAutomationUseAutomations(
    $automationId: ID!
    $retailerId: ID!
    $automation: UpdateAdAutomationInput!
  ) {
    updateAdAutomation(
      automationId: $automationId
      retailerId: $retailerId
      automation: $automation
    ) {
      id
      state
    }
  }
`

const CREATE_AD_AUTOMATIONS = gql`
  mutation createAdAutomationsUseAutomations(
    $params: [CreateAdAutomationsInput!]!
  ) {
    createAdAutomations(params: $params) {
      id
      state
    }
  }
`

const UPDATE_AD_AUTOMATIONS = gql`
  mutation updateAdAutomationsUseAutomations(
    $params: [UpdateAdAutomationsInput!]!
  ) {
    updateAdAutomations(params: $params) {
      id
      state
    }
  }
`

export function useAutomations(retailerAccount: RetailerAccount) {
  const [automations, setAutomations] = useState<Automation[] | null>(null)
  const [adAutomations, setAdAutomations] = useState<AdAutomation[] | null>(
    null,
  )
  const [, setErrors] = useState<Error[] | unknown[]>([])

  const retailerIds = retailerAccount?.retailers?.map((retailer) =>
    String(retailer.id),
  )

  const [
    getAdAutomationsByRetailerIdsQuery,
    getAdAutomationsByRetailerIdsResults,
  ] = useLazyQuery<Query>(GET_AD_AUTOMATIONS_BY_RETAILER_IDS)
  const [getAutomationsQuery, getAutomationsResults] =
    useLazyQuery<Query>(GET_AUTOMATIONS)

  const [createAdAutomationMutation] =
    useMutation<Mutation>(CREATE_AD_AUTOMATION)
  const [updateAdAutomationMutation] =
    useMutation<Mutation>(UPDATE_AD_AUTOMATION)
  const [createAdAutomationsMutation] = useMutation<Mutation>(
    CREATE_AD_AUTOMATIONS,
  )
  const [updateAdAutomationsMutation] = useMutation<Mutation>(
    UPDATE_AD_AUTOMATIONS,
  )

  const [createAutomationMutation] = useMutation<Mutation>(CREATE_AUTOMATION)
  const [createAutomationsMutation] = useMutation<Mutation>(CREATE_AUTOMATIONS)
  const [updateAutomationMutation] = useMutation<Mutation>(UPDATE_AUTOMATION)
  const [updateAutomationsMutation] = useMutation<Mutation>(UPDATE_AUTOMATIONS)

  //Just helpers to reuse. The idea is to keep things simple we want to
  //re-fetch these after mutations in order to ensure we have the latest.
  const refetchAutomations = useMemo(
    () => [
      {
        query: GET_AUTOMATIONS,
        variables: {
          resourceType: 'retailer_accounts',
          resourceId: retailerAccount?.id,
        },
      },
    ],
    [retailerAccount],
  )
  const refetchAdAutomations = useMemo(
    () => [
      {
        query: GET_AD_AUTOMATIONS_BY_RETAILER_IDS,
        variables: {
          retailerIds,
        },
      },
    ],
    [retailerIds],
  )

  useEffect(() => {
    setAutomations(
      (getAutomationsResults?.data?.getAutomations as Automation[]) || [],
    )
  }, [getAutomationsResults.data])

  useEffect(() => {
    setAdAutomations(
      (getAdAutomationsByRetailerIdsResults?.data
        ?.getAdAutomationsByRetailerIds as AdAutomation[]) || [],
    )
  }, [getAdAutomationsByRetailerIdsResults.data])

  const getAutomations = useCallback(
    async (params: string[]) => {
      return getAutomationsQuery({
        variables: {
          resourceType: 'retailer_accounts',
          resourceId: retailerAccount?.id,
          params,
        },
      })
        .then((response) => {
          setAutomations((response?.data?.getAutomations as Automation[]) || [])
        })
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [retailerAccount, getAutomationsQuery],
  )

  const getAdAutomations = useCallback(async () => {
    return getAdAutomationsByRetailerIdsQuery({
      variables: {
        retailerIds,
      },
    })
      .then((response) => {
        setAdAutomations(
          (response?.data?.getAdAutomationsByRetailerIds as AdAutomation[]) ||
            [],
        )
      })
      .catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
  }, [retailerIds, getAdAutomationsByRetailerIdsQuery])

  const createAdAutomation = useCallback(
    async (automation: Automation) => {
      return createAdAutomationMutation({
        variables: {
          automation,
        },
        refetchQueries: refetchAdAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [createAdAutomationMutation, refetchAdAutomations],
  )

  const updateAdAutomation = useCallback(
    async (
      automationId: string,
      retailerId: string,
      automation: Automation,
    ) => {
      return updateAdAutomationMutation({
        variables: {
          automationId,
          retailerId,
          automation,
        },
        refetchQueries: refetchAdAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [updateAdAutomationMutation, refetchAdAutomations],
  )

  const createAdAutomations = useCallback(
    async (params: CreateAdAutomationsInput[]) => {
      return createAdAutomationsMutation({
        variables: {
          params,
        },
        refetchQueries: refetchAdAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [createAdAutomationsMutation, refetchAdAutomations],
  )

  const updateAdAutomations = useCallback(
    async (params: UpdateAdAutomationsInput[]) => {
      return updateAdAutomationsMutation({
        variables: {
          params,
        },
        refetchQueries: refetchAdAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [updateAdAutomationsMutation, refetchAdAutomations],
  )

  const createAutomation = useCallback(
    async (retailerId: string, automation: Automation) => {
      return createAutomationMutation({
        variables: {
          retailerId,
          automation,
        },
        refetchQueries: refetchAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [createAutomationMutation, refetchAutomations],
  )

  const createAutomations = useCallback(
    async (params: MutationCreateAutomationsArgs) => {
      return createAutomationsMutation({
        variables: params,
        refetchQueries: refetchAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [createAutomationsMutation, refetchAutomations],
  )

  const updateAutomation = useCallback(
    async (automationId: string, state: UpdateAutomationInput['state']) => {
      return updateAutomationMutation({
        variables: {
          automationId,
          state,
        },
        refetchQueries: refetchAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [updateAutomationMutation, refetchAutomations],
  )

  const updateAutomations = useCallback(
    async (params: MutationUpdateAutomationsArgs) => {
      return updateAutomationsMutation({
        variables: params,
        refetchQueries: refetchAutomations,
      }).catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
    },
    [updateAutomationsMutation, refetchAutomations],
  )

  return {
    getAutomationsLoading: getAutomationsResults.loading,
    getAutomations,
    createAutomation,
    createAutomations,
    updateAutomation,
    updateAutomations,
    automations,
    getAdAutomations,
    createAdAutomation,
    createAdAutomations,
    updateAdAutomation,
    updateAdAutomations,
    adAutomations,
  }
}
